import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Button, Table, Badge, Modal, Form,
  Alert, InputGroup, Pagination, Spinner, Dropdown, OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import {
  Receipt, Search, Eye, Download, Printer, Filter, Calendar,
  FileText, DollarSign, User, Phone, Building, Trash2,
  RefreshCw, Plus, Edit
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useBoutique } from '../../contexts/BoutiqueContext';
import { useTheme } from '../../contexts/ThemeContext'; // ✅ AJOUT
import saleService from '../../services/saleService';
import InvoicePrintService from './InvoicePrintService';
import InvoiceExportService from './InvoiceExportService';
import InvoiceExportButton from './InvoiceExportButton';

const InvoiceManagement = () => {
  const { user } = useAuth();
  const { currentBoutique } = useBoutique();
  const { primaryColor, secondaryColor, logoUrl } = useTheme(); // ✅ AJOUT

  // États principaux
  const [sales, setSales] = useState([]);
  const [filteredSales, setFilteredSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // États pour filtres et recherche
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all'); // today, week, month, all
  const [statusFilter, setStatusFilter] = useState('all'); // paid, credit, all
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);

  // États pour modals et actions
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [printing, setPrinting] = useState(null);
  const [downloading, setDownloading] = useState(null);

  // ✅ FONCTION: Construire l'objet boutique complet
  const getBoutiqueData = () => {
    return {
      // Données du contexte Boutique
      ...currentBoutique,
      // Données du contexte Theme (priorité)
      primaryColor: primaryColor || currentBoutique?.primaryColor || currentBoutique?.primary_color || '#0d6efd',
      secondaryColor: secondaryColor || currentBoutique?.secondaryColor || currentBoutique?.secondary_color || '#6c757d',
      logoUrl: logoUrl || currentBoutique?.logoUrl || currentBoutique?.logo_url || null,
      // Informations complémentaires
      name: currentBoutique?.name || 'SmartBoutik',
      address: currentBoutique?.address || 'Adresse non spécifiée',
      phone: currentBoutique?.phone || currentBoutique?.telephone || null,
      email: currentBoutique?.email || null
    };
  };

  // Charger les données au montage
  useEffect(() => {
    loadSales();
  }, []);

  // Appliquer les filtres quand ils changent
  useEffect(() => {
    applyFilters();
  }, [sales, searchTerm, dateFilter, statusFilter]);

  // Charger les ventes (basées sur saleService)
  const loadSales = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🧾 Chargement des ventes pour factures...');
      
      const response = await saleService.getSales();
      
      if (response.success) {
        // ✅ FILTRER LES VENTES ANNULÉES - Ne garder que les ventes non annulées
        const activeSales = response.data.filter(sale => !sale.isCancelled);
        
        console.log(`📋 Ventes récupérées: ${response.data.length} total, ${activeSales.length} actives (${response.data.length - activeSales.length} annulées exclues)`);
        
        setSales(activeSales);
        setFilteredSales(activeSales);
      } else {
        setError(response.message || 'Erreur lors du chargement');
        setSales([]);
        setFilteredSales([]);
      }
    } catch (error) {
      console.error('❌ Erreur chargement factures:', error);
      setError('Erreur de connexion');
      setSales([]);
      setFilteredSales([]);
    } finally {
      setLoading(false);
    }
  };

  // Appliquer les filtres
  const applyFilters = () => {
    let filtered = [...sales];

    // ✅ SÉCURITÉ: Toujours exclure les ventes annulées (double vérification)
    filtered = filtered.filter(sale => !sale.isCancelled);

    // Filtre par recherche (nom client ou numéro vente)
    if (searchTerm.trim()) {
      filtered = filtered.filter(sale =>
        (sale.customer?.name || sale.customerName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (sale.saleNumber || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (sale.customer?.phone || sale.customerPhone || '').includes(searchTerm)
      );
    }

    // Filtre par date
    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      filtered = filtered.filter(sale => {
        const saleDate = new Date(sale.createdAt || sale.saleDate);
        
        switch (dateFilter) {
          case 'today':
            return saleDate >= today;
          case 'week':
            const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
            return saleDate >= weekAgo;
          case 'month':
            const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
            return saleDate >= monthAgo;
          default:
            return true;
        }
      });
    }

    // Filtre par statut de paiement
    if (statusFilter !== 'all') {
      filtered = filtered.filter(sale => {
        switch (statusFilter) {
          case 'paid':
            return !sale.isCredit || sale.isCreditPaid;
          case 'credit':
            return sale.isCredit && !sale.isCreditPaid;
          default:
            return true;
        }
      });
    }

    setFilteredSales(filtered);
    setCurrentPage(1); // Reset à la première page
  };

  // Formater la devise
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount || 0) + ' XOF';
  };

  // Formater la date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Visualiser une facture
  const handleViewInvoice = (sale) => {
    setSelectedSale(sale);
    setShowInvoiceModal(true);
  };

  // Imprimer une facture
  const handlePrintInvoice = async (sale) => {
    try {
      setPrinting(sale.id);

      const boutiqueData = getBoutiqueData();
      
      // Debug: Tester les données boutique
      const testResult = InvoicePrintService.testBoutiqueData(boutiqueData);

      const result = await InvoicePrintService.printInvoice(sale, boutiqueData);

      if (result.success) {
        setSuccess(`Facture ${sale.saleNumber} imprimée avec succès`);
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.message || 'Erreur lors de l\'impression');
        setTimeout(() => setError(null), 5000);
      }
    } catch (error) {
      console.error('❌ Erreur impression:', error);
      setError('Erreur lors de l\'impression');
      setTimeout(() => setError(null), 5000);
    } finally {
      setPrinting(null);
    }
  };

  // Télécharger une facture
  const handleDownloadInvoice = async (sale) => {
    try {
      setDownloading(sale.id);

      const boutiqueData = getBoutiqueData();

      const result = await InvoicePrintService.downloadInvoicePDF(sale, boutiqueData);

      if (result.success) {
        setSuccess(result.message);
        setTimeout(() => setSuccess(null), 5000);
      } else {
        setError(result.message || 'Erreur lors du téléchargement');
        setTimeout(() => setError(null), 5000);
      }
    } catch (error) {
      console.error('❌ Erreur téléchargement:', error);
      setError('Erreur lors du téléchargement');
      setTimeout(() => setError(null), 5000);
    } finally {
      setDownloading(null);
    }
  };

  // Ouvrir pour génération PDF manuelle
  const handleOpenForPDF = async (sale) => {
    try {

      const boutiqueData = getBoutiqueData();
      const result = await InvoicePrintService.openForPDFGeneration(sale, boutiqueData);

      if (result.success) {
        setSuccess(result.message);
        setTimeout(() => setSuccess(null), 8000);
      } else {
        setError(result.message || 'Erreur lors de l\'ouverture');
        setTimeout(() => setError(null), 5000);
      }
    } catch (error) {
      console.error('❌ Erreur ouverture PDF:', error);
      setError('Erreur lors de l\'ouverture');
      setTimeout(() => setError(null), 5000);
    }
  };

  // Fonction de prévisualisation
  const handlePreviewInvoice = (sale) => {
    const boutiqueData = getBoutiqueData();
    InvoicePrintService.previewInvoice(sale, boutiqueData);
  };

  // Obtenir le badge de statut
  const getStatusBadge = (sale) => {
    if (sale.isCredit && !sale.isCreditPaid) {
      return <Badge bg="warning">À crédit</Badge>;
    }
    if (sale.isCredit && sale.isCreditPaid) {
      return <Badge bg="success">Crédit soldé</Badge>;
    }
    return <Badge bg="primary">Payé</Badge>;
  };

  // Pagination
  const totalPages = Math.ceil(filteredSales.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedSales = filteredSales.slice(startIndex, startIndex + itemsPerPage);

  return (
    <Container fluid className="py-4">
      {/* En-tête */}
      <Row className="mb-4">
  <Col>
    <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
      <div className="flex-grow-1">
        <h2 className="fw-bold mb-2 d-flex align-items-center">
          <Receipt size={28} className="me-2 text-primary" />
          <span className="d-none d-sm-inline">Gestion des Factures</span>
          <span className="d-sm-none">Factures</span>
        </h2>
        <p className="text-muted mb-0 d-none d-md-block">
          Consultez et imprimez les factures de vos ventes
        </p>
      </div>
      
      <div className="d-flex gap-2 w-100 w-md-auto">
        <Button 
          variant="primary"
          onClick={loadSales}
          disabled={loading}
          className="flex-grow-1 flex-md-grow-0"
        >
          <RefreshCw size={16} className="me-2" />
          <span className="d-none d-sm-inline">Actualiser</span>
          <span className="d-sm-none">Refresh</span>
        </Button>
        
        <InvoiceExportButton
          sales={filteredSales}
          boutique={getBoutiqueData()}
        />
      </div>
    </div>
  </Col>
</Row>

      {/* Filtres et recherche */}
      <Row className="g-3">
      {/* Recherche - Pleine largeur sur mobile */}
      <Col xs={12} md={4}>
        <InputGroup>
          <InputGroup.Text>
            <Search size={16} />
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Rechercher client, numéro..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>
      </Col>

      {/* Filtres - 2 colonnes sur mobile */}
      <Col xs={6} md={3}>
        <Form.Select
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
        >
          <option value="all">Toutes périodes</option>
          <option value="today">Aujourd'hui</option>
          <option value="week">7 derniers jours</option>
          <option value="month">30 derniers jours</option>
        </Form.Select>
      </Col>

      <Col xs={6} md={3}>
        <Form.Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">Tous statuts</option>
          <option value="paid">Payé</option>
          <option value="credit">À crédit</option>
        </Form.Select>
      </Col>

      {/* Compteur de résultats */}
      <Col xs={12} md={2} className="d-flex align-items-center justify-content-md-end">
        <Badge bg="secondary" className="py-2 px-3">
          {filteredSales.length} facture{filteredSales.length > 1 ? 's' : ''}
        </Badge>
      </Col>
    </Row>

      {/* Messages d'état */}
      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert variant="success" className="mb-4">
          {success}
        </Alert>
      )}


      {/* Tableau des factures */}
      <Card>
        <Card.Body className="p-0">
          {loading ? (
  <div className="text-center py-5">
    <Spinner animation="border" variant="primary" />
    <p className="mt-3 text-muted">Chargement des factures...</p>
  </div>
) : filteredSales.length === 0 ? (
  <div className="text-center py-5">
    <Receipt size={48} className="text-muted mb-3" />
    <h5 className="text-muted">Aucune facture trouvée</h5>
    <p className="text-muted">
      {searchTerm || dateFilter !== 'all' || statusFilter !== 'all'
        ? 'Essayez de modifier vos filtres'
        : 'Aucune vente n\'a encore été enregistrée'
      }
    </p>
  </div>
) : (
  <>
    {/* ✅ VERSION DESKTOP - Tableau (≥ lg) */}
    <div className="d-none d-lg-block">
      <Table hover className="mb-0">
        <thead className="bg-light">
          <tr>
            <th>N° Facture</th>
            <th>Client</th>
            <th>Date</th>
            <th className="text-end">Montant</th>
            <th>Statut</th>
            <th>Vendeur</th>
            <th className="text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginatedSales.map((sale) => (
            <tr key={sale.id}>
              <td>
                <code className="text-primary fw-bold">
                  {sale.saleNumber}
                </code>
              </td>
              <td>
                <div>
                  <strong>{sale.customer?.name || sale.customerName || 'Client anonyme'}</strong>
                  {(sale.customer?.phone || sale.customerPhone) && (
                    <div className="small text-muted">
                      <Phone size={12} className="me-1" />
                      {sale.customer?.phone || sale.customerPhone}
                    </div>
                  )}
                </div>
              </td>
              <td>
                <small>{formatDate(sale.createdAt || sale.saleDate)}</small>
              </td>
              <td className="text-end">
                <strong className="text-success">
                  {formatCurrency(sale.total)}
                </strong>
              </td>
              <td>{getStatusBadge(sale)}</td>
              <td>
                <small>{sale.seller?.name || sale.sellerName || 'N/A'}</small>
              </td>
              <td>
                <div className="d-flex gap-2 justify-content-center">
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>Voir la facture</Tooltip>}
                  >
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleViewInvoice(sale)}
                    >
                      <Eye size={14} />
                    </Button>
                  </OverlayTrigger>

                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>Imprimer</Tooltip>}
                  >
                    <Button
                      variant="outline-info"
                      size="sm"
                      onClick={() => handlePrintInvoice(sale)}
                      disabled={printing === sale.id}
                    >
                      {printing === sale.id ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <Printer size={14} />
                      )}
                    </Button>
                  </OverlayTrigger>

                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>Télécharger</Tooltip>}
                  >
                    <Button
                      variant="outline-success"
                      size="sm"
                      onClick={() => handleDownloadInvoice(sale)}
                      disabled={downloading === sale.id}
                    >
                      {downloading === sale.id ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <Download size={14} />
                      )}
                    </Button>
                  </OverlayTrigger>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>

    {/* ✅ VERSION MOBILE/TABLETTE - Cartes (< lg) */}
    <div className="d-lg-none">
      {paginatedSales.map((sale) => (
        <Card key={sale.id} className="mb-3 shadow-sm">
          <Card.Body className="p-3">
            {/* En-tête de la carte */}
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div>
                <code className="bg-light px-2 py-1 rounded fw-bold text-primary">
                  {sale.saleNumber}
                </code>
                <div className="mt-2">
                  {getStatusBadge(sale)}
                </div>
              </div>
              
              <div className="text-end">
                <div className="fw-bold text-success fs-5">
                  {formatCurrency(sale.total)}
                </div>
                <small className="text-muted">
                  {formatDate(sale.createdAt || sale.saleDate)}
                </small>
              </div>
            </div>

            {/* Informations du client */}
            <Row className="mb-3">
              <Col xs={6}>
                <div className="small text-muted mb-1">
                  <User size={14} className="me-1" />
                  Client
                </div>
                <div className="fw-semibold">
                  {sale.customer?.name || sale.customerName || 'Client anonyme'}
                </div>
                {(sale.customer?.phone || sale.customerPhone) && (
                  <div className="small text-muted">
                    <Phone size={12} className="me-1" />
                    {sale.customer?.phone || sale.customerPhone}
                  </div>
                )}
              </Col>
              
              <Col xs={6}>
                <div className="small text-muted mb-1">
                  <User size={14} className="me-1" />
                  Vendeur
                </div>
                <div className="fw-semibold">
                  {sale.seller?.name || sale.sellerName || 'N/A'}
                </div>
              </Col>
            </Row>

            {/* Boutons d'actions */}
            <div className="d-flex gap-2">
              <Button
                variant="outline-primary"
                size="sm"
                className="flex-grow-1"
                onClick={() => handleViewInvoice(sale)}
              >
                <Eye size={16} className="me-1" />
                Voir
              </Button>
              
              <Button
                variant="outline-info"
                size="sm"
                onClick={() => handlePrintInvoice(sale)}
                disabled={printing === sale.id}
              >
                {printing === sale.id ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <Printer size={16} />
                )}
              </Button>
              
              <Button
                variant="outline-success"
                size="sm"
                onClick={() => handleDownloadInvoice(sale)}
                disabled={downloading === sale.id}
              >
                {downloading === sale.id ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <Download size={16} />
                )}
              </Button>
            </div>
          </Card.Body>
        </Card>
      ))}
    </div>

    {/* ===== PAGINATION RESPONSIVE (commune aux deux vues) ===== */}
    {totalPages > 1 && (
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-center p-3 border-top gap-3">
        {/* Compteur */}
        <small className="text-muted text-center text-md-start order-2 order-md-1">
          Affichage de {startIndex + 1} à{' '}
          {Math.min(startIndex + itemsPerPage, filteredSales.length)} sur{' '}
          {filteredSales.length} factures
        </small>
        
        {/* Contrôles de pagination */}
        <div className="order-1 order-md-2">
          {/* Version mobile */}
          <div className="d-md-none">
            <div className="d-flex align-items-center gap-2">
              <Button
                variant="outline-primary"
                size="sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                ‹ Préc
              </Button>
              
              <span className="mx-2 text-nowrap">
                Page {currentPage} / {totalPages}
              </span>
              
              <Button
                variant="outline-primary"
                size="sm"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Suiv ›
              </Button>
            </div>
          </div>

          {/* Version desktop */}
          <div className="d-none d-md-block">
            <Pagination className="mb-0">
              <Pagination.Prev
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              />
              
              {(() => {
                const pages = [];
                const maxVisiblePages = 5;
                
                if (totalPages <= maxVisiblePages) {
                  for (let i = 1; i <= totalPages; i++) {
                    pages.push(
                      <Pagination.Item
                        key={i}
                        active={currentPage === i}
                        onClick={() => setCurrentPage(i)}
                      >
                        {i}
                      </Pagination.Item>
                    );
                  }
                } else {
                  pages.push(
                    <Pagination.Item
                      key={1}
                      active={currentPage === 1}
                      onClick={() => setCurrentPage(1)}
                    >
                      1
                    </Pagination.Item>
                  );

                  if (currentPage > 3) {
                    pages.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
                  }

                  const start = Math.max(2, currentPage - 1);
                  const end = Math.min(totalPages - 1, currentPage + 1);

                  for (let i = start; i <= end; i++) {
                    pages.push(
                      <Pagination.Item
                        key={i}
                        active={currentPage === i}
                        onClick={() => setCurrentPage(i)}
                      >
                        {i}
                      </Pagination.Item>
                    );
                  }

                  if (currentPage < totalPages - 2) {
                    pages.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
                  }

                  pages.push(
                    <Pagination.Item
                      key={totalPages}
                      active={currentPage === totalPages}
                      onClick={() => setCurrentPage(totalPages)}
                    >
                      {totalPages}
                    </Pagination.Item>
                  );
                }
                
                return pages;
              })()}
              
              <Pagination.Next
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              />
            </Pagination>
          </div>
        </div>
      </div>
    )}
  </>
)}

        </Card.Body>
      </Card>

      {/* Modal de visualisation de facture */}
      <InvoicePreviewModal
        show={showInvoiceModal}
        onHide={() => setShowInvoiceModal(false)}
        sale={selectedSale}
        boutique={getBoutiqueData()}
        onPrint={handlePrintInvoice}
        onDownload={handleDownloadInvoice}
      />
    </Container>
  );
};

// Composant Modal de prévisualisation de facture
const InvoicePreviewModal = ({ show, onHide, sale, boutique, onPrint, onDownload }) => {
  if (!sale) return null;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount || 0) + ' XOF';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <Receipt size={24} className="me-2" />
          Facture #{sale.saleNumber}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="invoice-preview" style={{ 
          fontFamily: 'Arial, sans-serif',
          lineHeight: '1.4',
          color: '#333'
        }}>
          {/* En-tête de la boutique */}
          <div className="text-center mb-4 pb-3 border-bottom">
            <div className="d-flex justify-content-center align-items-center mb-2">
              {boutique?.logoUrl ? (
                <div className="me-3">
                  <img 
                    src={boutique.logoUrl} 
                    alt={boutique.name}
                    style={{ 
                      height: '60px', 
                      width: '60px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      border: `2px solid ${boutique?.primaryColor || '#0d6efd'}`
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }}
                  />
                  <div 
                    className="d-flex align-items-center justify-content-center me-3"
                    style={{ 
                      height: '60px', 
                      width: '60px',
                      background: `linear-gradient(135deg, ${boutique?.primaryColor || '#0d6efd'}, ${boutique?.secondaryColor || '#6c757d'})`,
                      color: 'white',
                      borderRadius: '8px',
                      fontSize: '24px',
                      fontWeight: 'bold',
                      display: 'none'
                    }}
                  >
                    {(boutique?.name || 'S').charAt(0)}
                  </div>
                </div>
              ) : (
                <div 
                  className="d-flex align-items-center justify-content-center me-3"
                  style={{ 
                    height: '60px', 
                    width: '60px',
                    background: `linear-gradient(135deg, ${boutique?.primaryColor || '#0d6efd'}, ${boutique?.secondaryColor || '#6c757d'})`,
                    color: 'white',
                    borderRadius: '8px',
                    fontSize: '24px',
                    fontWeight: 'bold'
                  }}
                >
                  {(boutique?.name || 'S').charAt(0)}
                </div>
              )}
              <div>
                <h3 className="mb-1" style={{ 
                  color: boutique?.primaryColor || '#0d6efd',
                  fontWeight: 'bold'
                }}>
                  {boutique?.name || 'SmartBoutik'}
                </h3>
                <p className="mb-0 text-muted small">
                  {boutique?.address || 'Adresse non spécifiée'}
                </p>
                {boutique?.phone && (
                  <p className="mb-0 text-muted small">
                    <Phone size={12} className="me-1" />
                    {boutique.phone}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Informations facture */}
          <Row className="mb-4">
            <Col md={6}>
              <h6 className="text-primary mb-2">Informations client</h6>
              <div>
                <strong>{sale.customer?.name || sale.customerName || 'Client anonyme'}</strong>
              </div>
              {(sale.customer?.phone || sale.customerPhone) && (
                <div className="text-muted small">
                  <Phone size={12} className="me-1" />
                  {sale.customer?.phone || sale.customerPhone}
                </div>
              )}
              {(sale.customer?.address || sale.customerAddress) && (
                <div className="text-muted small">
                  <Building size={12} className="me-1" />
                  {sale.customer?.address || sale.customerAddress}
                </div>
              )}
            </Col>
            <Col md={6} className="text-end">
              <h6 className="text-primary mb-2">Détails facture</h6>
              <div><strong>N° Facture:</strong> {sale.saleNumber}</div>
              <div><strong>Date:</strong> {formatDate(sale.createdAt || sale.saleDate)}</div>
              <div><strong>Vendeur:</strong> {sale.seller?.name || sale.sellerName || 'N/A'}</div>
            </Col>
          </Row>

          {/* Articles */}
          <div className="mb-4">
            <h6 className="text-primary mb-3">Articles vendus</h6>
            <Table size="sm" className="border">
              <thead style={{ backgroundColor: boutique?.primaryColor + '15' || '#e3f2fd' }}>
                <tr>
                  <th>Article</th>
                  <th className="text-center">Quantité</th>
                  <th className="text-end">Prix unitaire</th>
                  <th className="text-end">Total</th>
                </tr>
              </thead>
              <tbody>
                {(sale.items || sale.saleItems || []).map((item, index) => (
                  <tr key={index}>
                    <td>{item.productName || item.product_name || item.name}</td>
                    <td className="text-center">{item.quantity}</td>
                    <td className="text-end">{formatCurrency(item.unitPrice || item.unit_price || item.price)}</td>
                    <td className="text-end">
                      {formatCurrency((item.unitPrice || item.unit_price || item.price) * item.quantity)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* Totaux */}
          <Row>
            <Col md={6}>
              {sale.notes && (
                <div>
                  <h6 className="text-primary">Notes</h6>
                  <p className="small text-muted">{sale.notes}</p>
                </div>
              )}
            </Col>
            <Col md={6}>
              <div className="border-start ps-3">
                <div className="d-flex justify-content-between mb-2">
                  <span>Sous-total:</span>
                  <span>{formatCurrency((sale.total || 0) + (sale.discount || 0))}</span>
                </div>
                {sale.discount > 0 && (
                  <div className="d-flex justify-content-between mb-2 text-danger">
                    <span>Remise:</span>
                    <span>-{formatCurrency(sale.discount)}</span>
                  </div>
                )}
                <div className="d-flex justify-content-between mb-2 border-top pt-2">
                  <strong>Total à payer:</strong>
                  <strong style={{ color: boutique?.primaryColor || '#0d6efd' }}>
                    {formatCurrency(sale.total)}
                  </strong>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Mode de paiement:</span>
                  <Badge bg={sale.isCredit ? 'warning' : 'success'}>
                    {sale.isCredit ? 'À crédit' : (sale.paymentMethod || 'Espèces')}
                  </Badge>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Fermer
        </Button>
        <Button 
          variant="primary" 
          onClick={() => onPrint(sale)}
        >
          <Printer size={16} className="me-2" />
          Imprimer
        </Button>
        <Button 
          variant="success" 
          onClick={() => onDownload(sale)}
        >
          <Download size={16} className="me-2" />
          Télécharger
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

<style jsx>{`
/* ===== InvoiceManagement - CSS pour affichage cartes responsive ===== */

/* Cartes de factures sur mobile/tablette */
.invoice-card {
  border-radius: 0.5rem;
  transition: all 0.2s ease;
  border: 1px solid #e0e0e0;
}

.invoice-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.invoice-card .card-body {
  padding: 1rem;
}

/* En-tête de carte avec numéro de facture */
.invoice-card code {
  font-size: 0.875rem;
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  background-color: #e3f2fd;
  color: #1976d2;
  font-weight: 600;
  font-family: 'Courier New', monospace;
}

/* Montant de la facture */
.invoice-card .fs-5 {
  font-size: 1.25rem;
  line-height: 1.2;
}

/* Informations client et vendeur */
.invoice-card .small {
  font-size: 0.813rem;
  line-height: 1.4;
}

.invoice-card .fw-semibold {
  font-weight: 600;
  color: #212529;
}

/* Boutons d'actions dans les cartes */
.invoice-card .btn {
  font-size: 0.875rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.375rem;
  transition: all 0.2s ease;
}

.invoice-card .btn:hover {
  transform: scale(1.05);
}

.invoice-card .btn-outline-primary:hover {
  background-color: #0d6efd;
  color: white;
  border-color: #0d6efd;
}

.invoice-card .btn-outline-info:hover {
  background-color: #0dcaf0;
  color: white;
  border-color: #0dcaf0;
}

.invoice-card .btn-outline-success:hover {
  background-color: #198754;
  color: white;
  border-color: #198754;
}

/* Badges de statut */
.invoice-card .badge {
  font-size: 0.75rem;
  padding: 0.375rem 0.625rem;
  font-weight: 600;
  border-radius: 0.375rem;
}

/* Icônes dans les cartes */
.invoice-card svg {
  vertical-align: middle;
}

/* ===== Responsive Mobile ===== */
@media (max-width: 767px) {
  .invoice-card {
    margin-bottom: 0.75rem;
    border-radius: 0.375rem;
  }
  
  .invoice-card .card-body {
    padding: 0.75rem;
  }
  
  .invoice-card code {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
  }
  
  .invoice-card .fs-5 {
    font-size: 1.1rem;
  }
  
  .invoice-card .btn {
    padding: 0.375rem 0.5rem;
    font-size: 0.813rem;
  }
  
  .invoice-card .btn svg {
    width: 14px;
    height: 14px;
  }
  
  .invoice-card .small {
    font-size: 0.75rem;
  }
}

/* ===== Responsive Tablette ===== */
@media (min-width: 768px) and (max-width: 991px) {
  .invoice-card {
    margin-bottom: 1rem;
  }
  
  .invoice-card .card-body {
    padding: 0.875rem;
  }
}

/* ===== Tableau Desktop ===== */
@media (min-width: 992px) {
  .table {
    font-size: 0.938rem;
  }
  
  .table th {
    font-weight: 600;
    color: #495057;
    border-bottom: 2px solid #dee2e6;
    padding: 0.875rem 0.75rem;
  }
  
  .table td {
    padding: 0.875rem 0.75rem;
    vertical-align: middle;
  }
  
  .table tbody tr {
    transition: background-color 0.2s ease;
  }
  
  .table tbody tr:hover {
    background-color: #f8f9fa;
  }
  
  .table code {
    background-color: #e3f2fd;
    color: #1976d2;
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
    font-weight: 600;
  }
}

/* ===== Pagination responsive ===== */
.pagination {
  margin-bottom: 0;
}

.pagination .page-item .page-link {
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  border-radius: 0.375rem;
  margin: 0 0.125rem;
  transition: all 0.2s ease;
}

.pagination .page-item.active .page-link {
  background-color: #0d6efd;
  border-color: #0d6efd;
  font-weight: 600;
}

.pagination .page-item .page-link:hover {
  background-color: #e9ecef;
  border-color: #dee2e6;
}

.pagination .page-item.disabled .page-link {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Version mobile de la pagination */
@media (max-width: 767px) {
  .pagination-mobile {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
  }
  
  .pagination-mobile .btn {
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    font-weight: 600;
  }
  
  .pagination-mobile span {
    font-size: 0.875rem;
    font-weight: 600;
    color: #495057;
  }
}

/* ===== Messages d'état vide ===== */
.empty-state {
  padding: 3rem 1.5rem;
  text-align: center;
}

.empty-state svg {
  opacity: 0.5;
}

.empty-state h5 {
  margin-top: 1rem;
  margin-bottom: 0.5rem;
  color: #6c757d;
}

.empty-state p {
  color: #adb5bd;
}

@media (max-width: 767px) {
  .empty-state {
    padding: 2rem 1rem;
  }
  
  .empty-state svg {
    width: 40px;
    height: 40px;
  }
  
  .empty-state h5 {
    font-size: 1rem;
  }
  
  .empty-state p {
    font-size: 0.875rem;
  }
}

/* ===== Spinners ===== */
.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}

/* ===== Améliorations des badges ===== */
.badge {
  font-weight: 600;
  letter-spacing: 0.025em;
}

.badge-warning {
  background-color: #ffc107;
  color: #000;
}

.badge-success {
  background-color: #198754;
}

.badge-primary {
  background-color: #0d6efd;
}

/* ===== Animations ===== */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.invoice-card {
  animation: fadeIn 0.3s ease-out;
}

/* ===== Amélioration des tooltips (desktop uniquement) ===== */
@media (min-width: 992px) {
  .tooltip {
    font-size: 0.813rem;
  }
  
  .tooltip-inner {
    padding: 0.375rem 0.625rem;
    border-radius: 0.375rem;
  }
}

/* Désactiver les tooltips sur mobile */
@media (max-width: 991px) {
  .tooltip {
    display: none !important;
  }

}
`}</style>

export default InvoiceManagement;